import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { saveAs } from "file-saver";
import moment from "moment";
import MySegmented from "../../components/segmented/Segmented.jsx";
import columns from "../../columns/reports/filesReport.js";
import SkeletonTable from "../../components/table/SkeletonTable.jsx";
import View from "../../assets/View.jsx";
import AuditModal from "./components/AuditModal.jsx";
import {
  ExportFileReportApi,
  GetFileReportApi,
} from "../../api/request/report.js";
import BackButton from "../../components/button/BackButton.jsx";
import Templates from "../../assets/Templates.jsx";
import Delete from "../../assets/Delete.jsx";
import { DeleteServiceApi } from "../../api/request/service.js";
import ConfirmationModal from "../../components/modals/ConfirmationModal.jsx";
import LoadableButton from "../../components/button/LoadableButton.jsx";
import Export from "../../assets/Export.jsx";
import UserAvatar from "../../components/common/UserAvatar.jsx";
import Search from "../../components/common/Search.jsx";
import GlobalTooltip from "../../components/common/GlobalTooltip.jsx";
import ViewDocModal from "../files/components/ViewDocModal.jsx";

const FilesReport = () => {
  const { fileTypeId } = useParams();
  const navigate = useNavigate();
  const { state } = useLocation();
  const [segmentedValue, setSegmentedValue] = useState(
    state?.status || "unsubmitted"
  );
  const [fileList, setFileList] = useState([]);
  const [total, setTotal] = useState(0);
  const [totalRecords, setTotalRecords] = useState(0);
  const [count, setCount] = useState({});
  const [currentPage, setCurrentPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleteLoading, setIsDeleteLoading] = useState(false);
  const [isAuditModalOpen, setIsAuditModalOpen] = useState(false);
  const [isExportLoading, setIsExportLoading] = useState(false);
  const [isDocOpen, setIsDocOpen] = useState(false);
  const [selectedFileForAckDocs, setSelectedFileForAckDocs] = useState(null);

  const fileStatus = [
    {
      label: `Unsubmitted (${count?.unsubmitted_count || 0})`,
      value: "unsubmitted",
    },
    {
      label: `In-Process (${count?.inprocess_count || 0})`,
      value: "inprocess",
    },
    { label: `Closed (${count?.closed_count || 0})`, value: "closed" },
  ];

  const getFileList = async (searchText) => {
    try {
      setIsLoading(true);
      const params = {
        service_type_id: fileTypeId,
        file_status: segmentedValue,
        page: currentPage,
        search: searchText,
      };
      const { data } = await GetFileReportApi(params);
      if (data?.status) {
        setFileList(
          data?.data?.map((item) => {
            return {
              ...item,
              userDetails: <UserAvatar data={item?.user} />,
              file: (
                <div className="font-bold highlight-text capitalize">
                  {item?.service_name_slug}
                </div>
              ),
              startDate: item?.created_at
                ? moment(item?.created_at).format("lll")
                : "",
              ...(segmentedValue === "closed"
                ? {
                    acknowledge_ment_no: item?.acknowledge_ment_no,
                    ...(item?.acknowledge?.length && {
                      acknowledgement_doc: (
                        <a
                          className="text-blue-500 cursor-pointer"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedFileForAckDocs(item);
                            setIsDocOpen(true);
                          }}
                        >
                          View
                        </a>
                      ),
                    }),
                  }
                : null),
              action: (
                <div className="flex items-center gap-3">
                  <GlobalTooltip title={"View File Audit Report"}>
                    <button
                      onClick={() => {
                        setSelectedFile(item);
                        setIsAuditModalOpen(true);
                      }}
                    >
                      <Templates />
                    </button>
                  </GlobalTooltip>
                  {segmentedValue === "closed" && (
                    <GlobalTooltip title={"View Closed File"}>
                      <button
                        onClick={() =>
                          navigate(`/files_report/${fileTypeId}/${item?.id}`)
                        }
                      >
                        <View />
                      </button>
                    </GlobalTooltip>
                  )}
                  {segmentedValue !== "closed" && (
                    <GlobalTooltip title={"Delete File"}>
                      <button
                        onClick={() => {
                          setSelectedFile(item);
                          setIsDeleteModalOpen(true);
                        }}
                      >
                        <Delete />
                      </button>
                    </GlobalTooltip>
                  )}
                </div>
              ),
            };
          })
        );
        setTotal(data?.total_files);
        setTotalRecords(data?.total_records);
        setCount(data?.count);
      }
      setIsLoading(false);
    } catch (error) {
      setFileList([]);
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (segmentedValue) getFileList();
  }, [segmentedValue, currentPage]);

  const handleDeleteFile = async () => {
    if (!selectedFile?.id) return;
    try {
      setIsDeleteLoading(true);
      const { data } = await DeleteServiceApi({ file_id: selectedFile?.id });
      if (data?.status) {
        toast.success(data?.message);
        setIsDeleteModalOpen(false);
        setSelectedFile({});
        getFileList();
      }
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsDeleteLoading(false);
    }
  };

  const exportReport = async () => {
    try {
      setIsExportLoading(true);
      const headers = { responseType: "arraybuffer" };
      const params = { service_type_id: fileTypeId, type: segmentedValue };
      const { data } = await ExportFileReportApi(params, headers);
      const blob = new Blob([data], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const timestamp = new Date().toISOString();
      saveAs(blob, `report_${timestamp}.xlsx`);
    } catch (error) {
      if (error?.response?.data?.message)
        toast.error(error?.response?.data?.message);
    } finally {
      setIsExportLoading(false);
    }
  };

  const handleTableChange = (pagination) => {
    setCurrentPage(pagination?.current);
  };

  return (
    <div className="border rounded-md bg-white">
      <div className="flex items-center justify-between p-5">
        <div className="flex items-center gap-3">
          <BackButton />
          <h2 className="title">
            {state?.service} {totalRecords ? `(${totalRecords})` : null}
          </h2>
        </div>

        <div className="flex items-center gap-3">
          {["inprocess", "closed"].includes(segmentedValue) && (
            <LoadableButton
              className="!px-4"
              lable={"Export"}
              onClick={exportReport}
              isLoading={isExportLoading}
              loadingLable="Exporting..."
            >
              <Export /> Export
            </LoadableButton>
          )}
        </div>
      </div>

      <div className="flex flex-col gap-4">
        <div className="md:flex items-center md:justify-between px-3">
          <div className="overflow-x-auto">
            <MySegmented
              options={fileStatus}
              value={segmentedValue}
              setValue={setSegmentedValue}
            />
          </div>
          <div className="w-48 ml-auto md:mt-0 mt-4">
            <Search handleSearch={getFileList} />
          </div>
        </div>

        <div className="table-radius-0 text-light mb-4">
          <SkeletonTable
            columns={columns(segmentedValue)}
            data={fileList}
            isLoading={isLoading}
            pagination={{
              current: currentPage,
              total: total,
              pageSize: 10,
              showSizeChanger: false,
            }}
            handleTableChange={handleTableChange}
          />
        </div>
      </div>

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        setIsOpen={setIsDeleteModalOpen}
        message={`Are you sure you want to delete this file?`}
        onConfirm={handleDeleteFile}
        isLoading={isDeleteLoading}
        loadingLabel={`Deleting service...`}
      />

      <AuditModal
        file={selectedFile}
        isOpen={isAuditModalOpen}
        setIsOpen={setIsAuditModalOpen}
      />

      <ViewDocModal
        isDocOpen={isDocOpen}
        setIsDocOpen={setIsDocOpen}
        selectedFileForAckDocs={selectedFileForAckDocs}
      />
    </div>
  );
};

export default FilesReport;
